import { action, computed, extendObservable, isObservable, decorate, autorun } from "mobx";
import { AuthConstants } from "../constants/AuthConstants";
import _ from "lodash";
import { CommonConstants } from "../constants/CommonConstants";
import jwtDecode from "jwt-decode";
import { datadogRum } from "@datadog/browser-rum";

const ENV = process.env.REACT_APP_ENV;

class AuthStore {
  constructor(routerStore, authApi, oktaAuth) {
    this.routerStore = routerStore;
    this.authApi = authApi;
    this.oktaAuth = oktaAuth;
    this.oktaRefreshPromise = null;

    this.defaults = {
      loginService: null,
      accessToken: "",
      isLoggingIn: false,
      loginError: false,
      loggedInSession: false,
      mobileNavOpen: false,
      refreshInProgress: false,
      refreshToken: "",
      authSource: null,
      userData: {},
      isLoading: true,
      checkedLocalStorage: false
    };

    extendObservable(this, {
      loginService: this.defaults["loginService"],
      mobileNavOpen: this.defaults["mobileNavOpen"],
      accessToken: this.defaults["accessToken"],
      refreshToken: this.defaults["refreshToken"],
      loginError: this.defaults["loginError"],
      loggedInSession: this.defaults["loggedInSession"],
      isLoggingIn: this.defaults["isLoggingIn"],
      refreshInProgress: this.defaults["refreshInProgress"],
      authSource: this.defaults["authSource"],
      userData: this.defaults["userData"],
      isLoading: this.defaults["isLoading"],
      checkedLocalStorage: this.defaults["checkedLocalStorage"],
      checkLocalStorage: action(() => {
        this.userData = localStorage.getItem(AuthConstants.KEYS.USER_DATA)
          ? JSON.parse(localStorage.getItem(AuthConstants.KEYS.USER_DATA))
          : this.defaults["userData"];
        this.setDatadogUser(this.userData);
        this.refreshToken = localStorage.getItem(AuthConstants.KEYS.REFRESH_TOKEN);
        this.authSource = localStorage.getItem(AuthConstants.KEYS.AUTH_SOURCE);

        this.getActiveToken()
          .then(({ token, source }) => {
            this.accessToken = token;
            this.authSource = source;
            this.checkedLocalStorage = true;
          })
          .catch(() => {
            this.accessToken = "";
            this.authSource = null;
            this.checkedLocalStorage = true;
          });
      }),
      setLoginService: action(value => {
        this.loginService = value;
      }),
      setOktaSession: action(() => {
        this.authSource = AuthConstants.SOURCES.OKTA;
        this.clearCredentialsTokenStorage();
        localStorage.setItem(AuthConstants.KEYS.AUTH_SOURCE, AuthConstants.SOURCES.OKTA);
      }),
      setLoggedIn: action(value => {
        this.accessToken = value.access_token;
        this.refreshToken = value.refresh_token;
        this.authSource = AuthConstants.SOURCES.CREDENTIALS;
        this.loginService = null;
      }),
      setLocalStorage: action(() => {
        if (this.authSource === AuthConstants.SOURCES.CREDENTIALS && this.accessToken) {
          localStorage.setItem(AuthConstants.KEYS.ACCESS_TOKEN, this.accessToken);
        }
        if (this.authSource === AuthConstants.SOURCES.CREDENTIALS && this.refreshToken) {
          localStorage.setItem(AuthConstants.KEYS.REFRESH_TOKEN, this.refreshToken);
        }
        if (this.authSource) {
          localStorage.setItem(AuthConstants.KEYS.AUTH_SOURCE, this.authSource);
        }
        if (this.userData && this.userData.authorities) {
          localStorage.setItem(AuthConstants.KEYS.USER_DATA, JSON.stringify(this.userData));
        }
        if (this.userData && this.userData.org) {
          localStorage.setItem(CommonConstants.KEYS.SELECTED_ORG, JSON.stringify(this.userData.org));
        }
      }),
      setUserData: action(data => {
        this.userData = data;
        this.setDatadogUser(this.userData);
      }),
      setDatadogUser: action(data => {
        if (!!data) {
          datadogRum.setUser({
            id: data.userId?.toString(),
            email: data.email,
            name: `${data.lastName}, ${data.firstName}`,
            role: data.authorities?.map(authority => authority.name).join(", ")
          });
        } else {
          datadogRum.clearUser();
        }
      }),
      loginComplete: action(() => {
        this.loggedInSession = false;
      }),
      getUserInfo: action(() => {
        this.isLoading = true;
        return this.getActiveToken()
          .then(({ token, source, idToken }) => {
            this.accessToken = token;
            this.authSource = source;

            if (!token) {
              return null;
            }

            return this.authApi.getUserInfo().then(data => {
              if (data) {
                if (idToken?.idToken) {
                  data.oktaId = this.parseJwtSubject(idToken.idToken);
                }
                if (source === AuthConstants.SOURCES.OKTA) {
                  this.clearCredentialsTokenStorage();
                }
                this.setUserData(data);
                this.setLocalStorage();
                this.loggedInSession = true;
              }
              return data;
            });
          })
          .catch(() => null)
          .then(data => {
            this.isLoading = false;
            this.isLoggingIn = false;
            return data;
          });
      }),
      login: action((username, password) => {
        if (!this.isLoggingIn) {
          this.isLoggingIn = true;
          this.loginError = false;
          const body = {
            grant_type: "password",
            username: username,
            password: password,
            client_id: "comptix"
          };
          const encodedBody = this.serialize(body);
          this.authApi.getToken(encodedBody).then(data => {
            if (data && data.response && data.response.status === 401) {
              this.loginError = true;
              this.isLoggingIn = false;
              this.loginService = null;
              return;
            }
            if (!data || data.error || (data.data && data.data.error)) {
              this.loginError = true;
              this.isLoggingIn = false;
            } else {
              this.setLoggedIn(data);
              this.setLocalStorage();
            }
          });
        }
      }),
      logout: action(() => {
        this.clearAppSession();
        this.clearOktaSessionStorage();
        this.loggedInSession = false;
        this.loginService = null;
        datadogRum.clearUser();
        this.accessToken = "";
        this.refreshToken = "";
        this.authSource = null;
        this.userData = this.defaults["userData"];
        this.isLoading = false;
        this.routerStore.history.push("/");
      }),
      refresh: action(() => {
        if (this.refreshToken && !this.refreshInProgress) {
          this.refreshInProgress = true;
          const body = {
            grant_type: "refresh_token",
            refresh_token: this.refreshToken
          };
          const encodedBody = this.serialize(body);

          this.authApi.getToken(encodedBody).then(data => {
            if (data) {
              this.setLoggedIn(data);
              this.setLocalStorage();
            }
            this.refreshInProgress = false;
            this.routerStore.history.go(0);
          });
        }
      }),
      setMobileNavOpen: action(value => {
        this.mobileNavOpen = value;
      }),
      getLoginService: action(value => {
        this.authApi.getLoginService(value).then(data => {
          this.loginService = data;
        });
      })
    });

    autorun(() => {
      if (this.checkedLocalStorage) {
        if (this.accessToken?.length > 0) {
          this.getUserInfo();
        } else {
          this.isLoading = false;
        }
      }
    });

    autorun(() => {
      if (!this.checkedLocalStorage) {
        this.checkLocalStorage();
      }
    });
  }

  async getActiveToken() {
    if (this.authSource === AuthConstants.SOURCES.CREDENTIALS) {
      return {
        token: localStorage.getItem(AuthConstants.KEYS.ACCESS_TOKEN) || this.accessToken || "",
        source: AuthConstants.SOURCES.CREDENTIALS
      };
    }

    const oktaTokens = await this.getOktaTokens();
    if (this.hasOktaTokens(oktaTokens)) {
      const token = await this.getOktaAccessToken();
      return {
        token: token || "",
        source: AuthConstants.SOURCES.OKTA,
        idToken: oktaTokens.idToken
      };
    }

    const credentialsToken = localStorage.getItem(AuthConstants.KEYS.ACCESS_TOKEN) || "";
    return {
      token: credentialsToken,
      source: credentialsToken ? AuthConstants.SOURCES.CREDENTIALS : null
    };
  }

  async getBearerToken() {
    const { token, source } = await this.getActiveToken();
    this.accessToken = token;
    this.authSource = source;
    return token;
  }

  async getOktaTokens() {
    if (!this.oktaAuth) {
      return {};
    }

    try {
      return await this.oktaAuth.tokenManager.getTokens();
    } catch (e) {
      return {};
    }
  }

  hasOktaTokens(tokens) {
    return !!(tokens?.accessToken || tokens?.idToken || tokens?.refreshToken);
  }

  async getOktaAccessToken() {
    if (!this.oktaAuth) {
      return null;
    }

    try {
      return (await this.oktaAuth.getOrRenewAccessToken()) || this.oktaAuth.getAccessToken();
    } catch (e) {
      return null;
    }
  }

  refreshOktaAccessToken() {
    if (!this.oktaAuth) {
      return Promise.resolve(null);
    }

    if (!this.oktaRefreshPromise) {
      this.oktaRefreshPromise = this.oktaAuth.tokenManager
        .renew("accessToken")
        .then(token => {
          this.accessToken = token?.accessToken || "";
          this.authSource = AuthConstants.SOURCES.OKTA;
          return this.accessToken;
        })
        .catch(() => null)
        .then(token => {
          this.oktaRefreshPromise = null;
          return token;
        });
    }

    return this.oktaRefreshPromise;
  }

  clearCredentialsTokenStorage() {
    localStorage.removeItem(AuthConstants.KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AuthConstants.KEYS.REFRESH_TOKEN);
  }

  clearAppSession() {
    Object.keys(AuthConstants.KEYS).forEach(key => localStorage.removeItem(AuthConstants.KEYS[key]));
    localStorage.removeItem(CommonConstants.KEYS.SELECTED_ORG);
  }

  clearOktaSessionStorage() {
    AuthConstants.OKTA_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
  }
  get isProd() {
    return !!ENV && ENV.toLowerCase() === "prod";
  }

  // update for okta, set okta access token
  get loggedIn() {
    return !!this.accessToken && this.accessToken.length > 0 && !!this.userData && !!this.userData.authorities;
  }

  get isAdmin() {
    let authorities = isObservable(this.userData.authorities)
      ? this.userData.authorities.toJSON()
      : this.userData.authorities;
    return (
      this.loggedIn &&
      _.some(authorities, authority => {
        return authority.authority.toLowerCase() === "global" || authority.authority.toLowerCase() === "admin";
      }) &&
      !_.some(authorities, authority => {
        return authority.authority.toLowerCase() === "investigations";
      })
    );
  }

  get isGlobal() {
    let authorities = isObservable(this.userData.authorities)
      ? this.userData.authorities.toJSON()
      : this.userData.authorities;
    return (
      this.loggedIn &&
      _.some(authorities, authority => {
        return authority.authority.toLowerCase() === "global";
      }) &&
      !_.some(authorities, authority => {
        return authority.authority.toLowerCase() === "investigations";
      })
    );
  }

  get isTicketing() {
    let authorities = isObservable(this.userData.authorities)
      ? this.userData.authorities.toJSON()
      : this.userData.authorities;
    return (
      this.loggedIn &&
      _.some(authorities, authority => {
        return authority.authority.toLowerCase() === "ticketing";
      }) &&
      !_.some(authorities, authority => {
        return authority.authority.toLowerCase() === "investigations";
      })
    );
  }

  get isInvestigations() {
    let authorities = isObservable(this.userData.authorities)
      ? this.userData.authorities.toJSON()
      : this.userData.authorities;
    return (
      this.loggedIn &&
      _.some(authorities, authority => {
        return authority.authority.toLowerCase() === "investigations";
      })
    );
  }

  get isUmpireAttendant() {
    let authorities = isObservable(this.userData.authorities)
      ? this.userData.authorities.toJSON()
      : this.userData.authorities;
    return (
      this.loggedIn &&
      _.some(authorities, authority => {
        return authority.authority.toLowerCase() === "umpire attendant";
      }) &&
      !_.some(authorities, authority => {
        return authority.authority.toLowerCase() === "investigations";
      })
    );
  }

  get isBoxOfficeAdmin() {
    let authorities = isObservable(this.userData.authorities)
      ? this.userData.authorities.toJSON()
      : this.userData.authorities;

    return (
      this.loggedIn &&
      _.some(authorities, authority => {
        return authority.authority.toLowerCase() === "box office admin";
      }) &&
      !_.some(authorities, authority => {
        return authority.authority.toLowerCase() === "investigations";
      })
    );
  }

  get currentOrg() {
    if (this.userData && this.userData.org) {
      return this.userData.org;
    } else {
      return {};
    }
  }

  get currentUserId() {
    if (this.userData && this.userData.userId) {
      return this.userData.userId;
    } else {
      return -1;
    }
  }

  get firstCommaLast() {
    return this.userData && this.userData.firstName ? this.userData.firstName + ", " + this.userData.lastName : "";
  }

  serialize(obj) {
    let str = [];
    for (let p in obj)
      if (obj.hasOwnProperty(p)) {
        str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
      }
    return str.join("&");
  }

  parseJwtSubject(token) {
    try {
      const decoded = jwtDecode(token);
      return decoded.sub || "";
    } catch (e) {
      return "";
    }
  }
}

decorate(AuthStore, {
  isProd: computed,
  loggedIn: computed,
  isAdmin: computed,
  isGlobal: computed,
  isTicketing: computed,
  isBoxOfficeAdmin: computed,
  currentOrg: computed,
  currentUserId: computed,
  firstCommaLast: computed,
  isInvestigations: computed,
  isUmpireAttendant: computed
});

export default AuthStore;
